// src/pages/Drivers.js - Updated with Export + Bulk Actions
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import ExportButton from '../components/ExportButton';
import BulkActions from '../components/BulkActions';
import DateRangeFilter from '../components/DateRangeFilter';

function Drivers() {
  const [drivers, setDrivers] = useState([]);
  const [filteredDrivers, setFilteredDrivers] = useState([]);
  const [pendingDrivers, setPendingDrivers] = useState([]);
  const [filteredPending, setFilteredPending] = useState([]);
  const [activeTab, setActiveTab] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [approving, setApproving] = useState(false);
  const [error, setError] = useState(null);
  
  // Bulk actions state
  const [selectedDrivers, setSelectedDrivers] = useState([]);
  
  // Date range state
  const [dateRange, setDateRange] = useState(null);

  const API_URL = 'https://boda-backend-wuea.onrender.com/api/admin';
  const navigate = useNavigate();

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    filterDrivers();
  }, [searchTerm, drivers, pendingDrivers, statusFilter, dateRange]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      
      const [pendingRes, allRes] = await Promise.all([
        axios.get(`${API_URL}/pending-drivers`, { headers }),
        axios.get(`${API_URL}/all-drivers`, { headers }),
      ]);
      
      setPendingDrivers(pendingRes.data.drivers || []);
      setFilteredPending(pendingRes.data.drivers || []);
      setDrivers(allRes.data.drivers || []);
      setFilteredDrivers(allRes.data.drivers || []);
      setSelectedDrivers([]);
    } catch (error) {
      console.error('Error fetching drivers:', error);
      setError('Failed to load drivers. Check if backend is running.');
    } finally {
      setLoading(false);
    }
  };

  const filterDrivers = () => {
    let allList = [...drivers];
    let pendingList = [...pendingDrivers];
    
    // Apply date filter
    if (dateRange && dateRange.startDate && dateRange.endDate) {
      const start = new Date(dateRange.startDate);
      const end = new Date(dateRange.endDate);
      end.setHours(23, 59, 59);
      const filterFn = (driver) => {
        const date = new Date(driver.created_at);
        return date >= start && date <= end;
      };
      allList = allList.filter(filterFn);
      pendingList = pendingList.filter(filterFn);
    }
    
    // Apply status filter for all drivers
    if (statusFilter !== 'all') {
      allList = allList.filter(driver => {
        if (statusFilter === 'blocked') return driver.is_blocked === true;
        if (statusFilter === 'suspended') return driver.is_suspended === true;
        if (statusFilter === 'terminated') return driver.is_terminated === true;
        if (statusFilter === 'active') return !driver.is_blocked && !driver.is_suspended && !driver.is_terminated;
        return true;
      });
    }
    
    // Apply search filter
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase().trim();
      const filterFn = (driver) => {
        const name = (driver.full_name || '').toLowerCase();
        const phone = (driver.phone_number || '').toLowerCase();
        const email = (driver.email || '').toLowerCase();
        const id = String(driver.id || '');
        const vehicle = (driver.vehicle_type || '').toLowerCase();
        const status = (driver.verification_status || '').toLowerCase();
        
        return name.includes(term) || 
               phone.includes(term) || 
               email.includes(term) || 
               id.includes(term) ||
               vehicle.includes(term) ||
               status.includes(term);
      };
      
      allList = allList.filter(filterFn);
      pendingList = pendingList.filter(filterFn);
    }
    
    setFilteredDrivers(allList);
    setFilteredPending(pendingList);
  };

  const approveDriver = async (driverId) => {
    if (!window.confirm('Approve this driver?')) return;
    
    setApproving(true);
    try {
      await axios.put(`${API_URL}/approve-driver/${driverId}`);
      alert('Driver approved successfully!');
      fetchData();
    } catch (error) {
      alert('Failed to approve driver: ' + (error.response?.data?.error || error.message));
    } finally {
      setApproving(false);
    }
  };

  const rejectDriver = async (driverId) => {
    const reason = prompt('Enter rejection reason:');
    if (!reason) return;
    
    setApproving(true);
    try {
      await axios.put(`${API_URL}/reject-driver/${driverId}`, { reason });
      alert('Driver rejected');
      fetchData();
    } catch (error) {
      alert('Failed to reject driver: ' + (error.response?.data?.error || error.message));
    } finally {
      setApproving(false);
    }
  };

  const goToDriverDetails = (driverId) => {
    navigate(`/drivers/${driverId}`);
  };

  const getDriverAccountStatus = (driver) => {
    if (driver.is_terminated) return { status: 'terminated', label: '⚠️ Terminated', color: '#dc2626', bg: '#fee2e2' };
    if (driver.is_suspended) return { status: 'suspended', label: '⏸️ Suspended', color: '#f59e0b', bg: '#fef3c7' };
    if (driver.is_blocked) return { status: 'blocked', label: '🚫 Blocked', color: '#ef4444', bg: '#fee2e2' };
    return { status: 'active', label: '✅ Active', color: '#10b981', bg: '#dcfce7' };
  };

  const getSearchStats = () => {
    if (!searchTerm && statusFilter === 'all' && !dateRange) return null;
    const currentList = activeTab === 'pending' ? filteredPending : filteredDrivers;
    const totalList = activeTab === 'pending' ? pendingDrivers : drivers;
    return {
      shown: currentList.length,
      total: totalList.length,
      hidden: totalList.length - currentList.length
    };
  };

  const getStatusCounts = () => {
    const counts = {
      all: drivers.length,
      active: 0,
      blocked: 0,
      suspended: 0,
      terminated: 0,
    };
    drivers.forEach(d => {
      if (d.is_terminated) counts.terminated++;
      else if (d.is_suspended) counts.suspended++;
      else if (d.is_blocked) counts.blocked++;
      else counts.active++;
    });
    return counts;
  };

  // Export data
  const getExportData = () => {
    const list = activeTab === 'pending' ? filteredPending : filteredDrivers;
    return list.map(d => ({
      ID: d.id,
      Name: d.full_name || '—',
      Phone: d.phone_number,
      Email: d.email || '—',
      Vehicle: d.vehicle_type || '—',
      'Verification Status': d.verification_status || '—',
      'Account Status': d.is_terminated ? 'Terminated' : d.is_suspended ? 'Suspended' : d.is_blocked ? 'Blocked' : 'Active',
      'Is Online': d.is_online ? 'Yes' : 'No',
      Rating: d.average_rating || '—',
      'Total Rides': d.total_rides || 0,
      Registered: new Date(d.created_at).toLocaleDateString(),
    }));
  };

  // Handle select all
  const handleSelectAll = (checked, list) => {
    if (checked) {
      setSelectedDrivers(list.map(d => d.id));
    } else {
      setSelectedDrivers([]);
    }
  };

  // Handle individual select
  const handleSelectDriver = (driverId, checked) => {
    if (checked) {
      setSelectedDrivers([...selectedDrivers, driverId]);
    } else {
      setSelectedDrivers(selectedDrivers.filter(id => id !== driverId));
    }
  };

  const statusCounts = getStatusCounts();

  const renderDriverTable = (driverList, showActions = false) => {
    const stats = getSearchStats();
    const isPendingTab = activeTab === 'pending';
    
    return (
      <>
        {searchTerm && stats && (
          <div style={{
            display: 'flex',
            gap: '12px',
            marginBottom: '12px',
            flexWrap: 'wrap',
            alignItems: 'center',
          }}>
            <span style={{ fontSize: '13px', color: '#64748b' }}>
              🔍 Showing {stats.shown} of {stats.total} drivers
              {stats.hidden > 0 && ` (${stats.hidden} hidden)`}
            </span>
            {stats.hidden > 0 && (
              <button
                onClick={() => setSearchTerm('')}
                style={{
                  fontSize: '12px',
                  color: '#f97316',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  textDecoration: 'underline',
                }}
              >
                Clear Search
              </button>
            )}
          </div>
        )}
        
        <div style={{ overflow: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #e2e8f0', backgroundColor: '#f8fafc' }}>
                <th style={{ textAlign: 'left', padding: '12px', fontWeight: '600' }}>
                  <input
                    type="checkbox"
                    checked={selectedDrivers.length === driverList.length && driverList.length > 0}
                    onChange={(e) => handleSelectAll(e.target.checked, driverList)}
                  />
                </th>
                <th style={{ textAlign: 'left', padding: '12px', fontWeight: '600' }}>ID</th>
                <th style={{ textAlign: 'left', padding: '12px', fontWeight: '600' }}>Name</th>
                <th style={{ textAlign: 'left', padding: '12px', fontWeight: '600' }}>Phone</th>
                <th style={{ textAlign: 'left', padding: '12px', fontWeight: '600' }}>Vehicle</th>
                <th style={{ textAlign: 'left', padding: '12px', fontWeight: '600' }}>Verification</th>
                <th style={{ textAlign: 'left', padding: '12px', fontWeight: '600' }}>Account Status</th>
                <th style={{ textAlign: 'left', padding: '12px', fontWeight: '600' }}>Registered</th>
                {showActions && <th style={{ textAlign: 'left', padding: '12px', fontWeight: '600' }}>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {driverList.length === 0 ? (
                <tr>
                  <td colSpan={showActions ? 9 : 8} style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>
                    {searchTerm ? `No drivers found matching "${searchTerm}"` : 'No drivers found'}
                  </td>
                </tr>
              ) : (
                driverList.map((driver) => {
                  const accountStatus = getDriverAccountStatus(driver);
                  return (
                    <tr key={driver.id} style={{ borderBottom: '1px solid #e2e8f0' }}>
                      <td style={{ padding: '12px' }}>
                        <input
                          type="checkbox"
                          checked={selectedDrivers.includes(driver.id)}
                          onChange={(e) => handleSelectDriver(driver.id, e.target.checked)}
                        />
                      </td>
                      <td style={{ padding: '12px' }}>#{driver.id}</td>
                      <td style={{ padding: '12px' }}>
                        <button
                          onClick={() => goToDriverDetails(driver.id)}
                          style={{
                            background: 'none',
                            border: 'none',
                            color: '#3b82f6',
                            cursor: 'pointer',
                            textDecoration: 'underline',
                            fontSize: '14px',
                          }}
                        >
                          {driver.full_name || '—'}
                        </button>
                      </td>
                      <td style={{ padding: '12px' }}>{driver.phone_number}</td>
                      <td style={{ padding: '12px' }}>
                        {driver.vehicle_type === 'boda' ? '🛵 Boda' :
                         driver.vehicle_type === 'boda_electric' ? '⚡ Boda Electric' :
                         driver.vehicle_type === 'car' ? '🚗 Car' :
                         driver.vehicle_type === 'car_xl' ? '🚙 Car XL' : '—'}
                      </td>
                      <td style={{ padding: '12px' }}>
                        <span style={{
                          padding: '4px 8px',
                          borderRadius: '12px',
                          fontSize: '11px',
                          backgroundColor: driver.is_approved ? '#dcfce7' : 
                                           driver.verification_status === 'pending' ? '#fef3c7' : '#fee2e2',
                          color: driver.is_approved ? '#166534' : 
                                 driver.verification_status === 'pending' ? '#92400e' : '#991b1b',
                        }}>
                          {driver.is_approved ? 'Approved' : 
                           driver.verification_status === 'pending' ? 'Pending' : 'Rejected'}
                        </span>
                      </td>
                      <td style={{ padding: '12px' }}>
                        <span style={{
                          padding: '4px 8px',
                          borderRadius: '12px',
                          fontSize: '11px',
                          backgroundColor: accountStatus.bg,
                          color: accountStatus.color,
                          fontWeight: '500',
                        }}>
                          {accountStatus.label}
                        </span>
                      </td>
                      <td style={{ padding: '12px' }}>{new Date(driver.created_at).toLocaleDateString()}</td>
                      {showActions && (
                        <td style={{ padding: '12px' }}>
                          <button
                            onClick={() => approveDriver(driver.id)}
                            disabled={approving}
                            style={{
                              padding: '6px 12px',
                              backgroundColor: '#22c55e',
                              color: 'white',
                              border: 'none',
                              borderRadius: '6px',
                              cursor: approving ? 'not-allowed' : 'pointer',
                              marginRight: '6px',
                              opacity: approving ? 0.6 : 1,
                              fontSize: '12px',
                            }}
                          >
                            Approve
                          </button>
                          <button
                            onClick={() => rejectDriver(driver.id)}
                            disabled={approving}
                            style={{
                              padding: '6px 12px',
                              backgroundColor: '#ef4444',
                              color: 'white',
                              border: 'none',
                              borderRadius: '6px',
                              cursor: approving ? 'not-allowed' : 'pointer',
                              opacity: approving ? 0.6 : 1,
                              fontSize: '12px',
                            }}
                          >
                            Reject
                          </button>
                        </td>
                      )}
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </>
    );
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '40px', marginBottom: '16px' }}>🔄</div>
          <div style={{ fontSize: '18px', color: '#64748b' }}>Loading drivers...</div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: '0 0 20px 0' }}>
      {/* Header */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        marginBottom: '20px',
        flexWrap: 'wrap',
        gap: '12px',
      }}>
        <div>
          <h1 style={{ margin: 0, fontSize: '24px', fontWeight: 'bold' }}>Drivers Management</h1>
          <div style={{ display: 'flex', gap: '16px', marginTop: '4px', fontSize: '14px', color: '#64748b', flexWrap: 'wrap' }}>
            <span>🛵 {drivers.length} total</span>
            <span style={{ color: '#f59e0b' }}>⏳ {pendingDrivers.length} pending</span>
            <span style={{ color: '#10b981' }}>✅ {statusCounts.active} active</span>
            <span style={{ color: '#ef4444' }}>🚫 {statusCounts.blocked} blocked</span>
            <span style={{ color: '#f59e0b' }}>⏸️ {statusCounts.suspended} suspended</span>
            <span style={{ color: '#dc2626' }}>⚠️ {statusCounts.terminated} terminated</span>
            {selectedDrivers.length > 0 && (
              <span style={{ color: '#3b82f6' }}>
                ☑️ {selectedDrivers.length} selected
              </span>
            )}
          </div>
        </div>
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
          {/* Date Range Filter */}
          <DateRangeFilter 
            onApply={(range) => setDateRange(range)}
            onClear={() => setDateRange(null)}
            label="Filter by Date"
          />
          
          {/* Export Button */}
          <ExportButton 
            data={getExportData()} 
            filename={`drivers_${activeTab}`} 
            label="Export CSV"
          />
          
          {/* Bulk Actions */}
          <BulkActions 
            selectedIds={selectedDrivers}
            onActionComplete={() => {
              setSelectedDrivers([]);
              fetchData();
            }}
            type="drivers"
            API_URL={API_URL}
          />
          
          {/* Search */}
          <div style={{ position: 'relative' }}>
            <input
              type="text"
              placeholder="🔍 Search drivers..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                padding: '10px 16px',
                paddingRight: searchTerm ? '40px' : '16px',
                borderRadius: '10px',
                border: '2px solid #e2e8f0',
                width: '280px',
                fontSize: '14px',
                outline: 'none',
                transition: 'border-color 0.2s',
              }}
              onFocus={(e) => e.target.style.borderColor = '#f97316'}
              onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                style={{
                  position: 'absolute',
                  right: '10px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '16px',
                  color: '#94a3b8',
                }}
              >
                ✕
              </button>
            )}
          </div>
          
          <button
            onClick={fetchData}
            style={{
              padding: '10px 20px',
              backgroundColor: '#f97316',
              color: 'white',
              border: 'none',
              borderRadius: '10px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500',
            }}
          >
            🔄 Refresh
          </button>
        </div>
      </div>

      {error && (
        <div style={{
          backgroundColor: '#fee2e2',
          color: '#991b1b',
          padding: '14px 18px',
          borderRadius: '10px',
          marginBottom: '16px',
          border: '1px solid #fecaca',
        }}>
          ⚠️ {error}
        </div>
      )}

      {/* Status Filter Buttons */}
      <div style={{
        display: 'flex',
        gap: '8px',
        flexWrap: 'wrap',
        marginBottom: '16px',
      }}>
        <button
          onClick={() => setStatusFilter('all')}
          style={{
            padding: '6px 16px',
            borderRadius: '20px',
            border: statusFilter === 'all' ? '2px solid #f97316' : '1px solid #e2e8f0',
            backgroundColor: statusFilter === 'all' ? '#f97316' : 'white',
            color: statusFilter === 'all' ? 'white' : '#64748b',
            cursor: 'pointer',
            fontSize: '13px',
            fontWeight: statusFilter === 'all' ? '600' : '400',
          }}
        >
          All ({statusCounts.all})
        </button>
        <button
          onClick={() => setStatusFilter('active')}
          style={{
            padding: '6px 16px',
            borderRadius: '20px',
            border: statusFilter === 'active' ? '2px solid #10b981' : '1px solid #e2e8f0',
            backgroundColor: statusFilter === 'active' ? '#10b981' : 'white',
            color: statusFilter === 'active' ? 'white' : '#64748b',
            cursor: 'pointer',
            fontSize: '13px',
            fontWeight: statusFilter === 'active' ? '600' : '400',
          }}
        >
          ✅ Active ({statusCounts.active})
        </button>
        <button
          onClick={() => setStatusFilter('blocked')}
          style={{
            padding: '6px 16px',
            borderRadius: '20px',
            border: statusFilter === 'blocked' ? '2px solid #ef4444' : '1px solid #e2e8f0',
            backgroundColor: statusFilter === 'blocked' ? '#ef4444' : 'white',
            color: statusFilter === 'blocked' ? 'white' : '#64748b',
            cursor: 'pointer',
            fontSize: '13px',
            fontWeight: statusFilter === 'blocked' ? '600' : '400',
          }}
        >
          🚫 Blocked ({statusCounts.blocked})
        </button>
        <button
          onClick={() => setStatusFilter('suspended')}
          style={{
            padding: '6px 16px',
            borderRadius: '20px',
            border: statusFilter === 'suspended' ? '2px solid #f59e0b' : '1px solid #e2e8f0',
            backgroundColor: statusFilter === 'suspended' ? '#f59e0b' : 'white',
            color: statusFilter === 'suspended' ? 'white' : '#64748b',
            cursor: 'pointer',
            fontSize: '13px',
            fontWeight: statusFilter === 'suspended' ? '600' : '400',
          }}
        >
          ⏸️ Suspended ({statusCounts.suspended})
        </button>
        <button
          onClick={() => setStatusFilter('terminated')}
          style={{
            padding: '6px 16px',
            borderRadius: '20px',
            border: statusFilter === 'terminated' ? '2px solid #dc2626' : '1px solid #e2e8f0',
            backgroundColor: statusFilter === 'terminated' ? '#dc2626' : 'white',
            color: statusFilter === 'terminated' ? 'white' : '#64748b',
            cursor: 'pointer',
            fontSize: '13px',
            fontWeight: statusFilter === 'terminated' ? '600' : '400',
          }}
        >
          ⚠️ Terminated ({statusCounts.terminated})
        </button>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '4px', marginBottom: '20px', borderBottom: '1px solid #e2e8f0' }}>
        <button
          onClick={() => setActiveTab('all')}
          style={{
            padding: '12px 24px',
            backgroundColor: activeTab === 'all' ? '#f97316' : 'transparent',
            color: activeTab === 'all' ? 'white' : '#64748b',
            border: 'none',
            borderRadius: '8px 8px 0 0',
            cursor: 'pointer',
            fontWeight: activeTab === 'all' ? '600' : '400',
          }}
        >
          All Drivers ({filteredDrivers.length})
        </button>
        <button
          onClick={() => setActiveTab('pending')}
          style={{
            padding: '12px 24px',
            backgroundColor: activeTab === 'pending' ? '#f97316' : 'transparent',
            color: activeTab === 'pending' ? 'white' : '#64748b',
            border: 'none',
            borderRadius: '8px 8px 0 0',
            cursor: 'pointer',
            fontWeight: activeTab === 'pending' ? '600' : '400',
          }}
        >
          Pending Review ({filteredPending.length})
        </button>
      </div>

      {/* Content */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '20px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
      }}>
        {activeTab === 'all' && (
          <>
            <h3 style={{ marginBottom: '16px' }}>All Drivers</h3>
            {renderDriverTable(filteredDrivers, false)}
          </>
        )}
        {activeTab === 'pending' && (
          <>
            <h3 style={{ marginBottom: '16px' }}>Driver Applications Pending Review</h3>
            {renderDriverTable(filteredPending, true)}
          </>
        )}
      </div>
    </div>
  );
}

export default Drivers;